import React, { useEffect, useState, useRef } from 'react';
import { LinkSearchModal, ContactPickerModal, DuplicateWarning, findPossibleDuplicates, duplicateKey, PatientSideUnlink } from '../components/PatientContactLink';
import { Search, User, Phone, Mail, Building, Plus, Edit3, Save, X, Trash2, ChevronLeft, AlertTriangle, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import PrintButton from '../components/PrintButton';

const EMPTY_FORM = {
  firstName: '', lastName: '', dateOfBirth: '', gender: 'U',
  phone: '', email: '', address: '', city: '', state: '', zip: '',
  insuranceProvider: '', insuranceId: '', primaryPhysicianId: '',
  // USCDI Demographics
  race: '', ethnicity: '', preferredLanguage: '', sexualOrientation: '', genderIdentity: '',
  emergencyContactName: '', emergencyContactPhone: '', emergencyContactRelation: '', ssn4: '',
  // ICD-10-CM & SNOMED CT
  icd10Code: '', icd10Description: '', isPrimaryDiagnosis: false, snomedCode: '',
};

const GENDER_OPTIONS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'O', label: 'Other' },
  { value: 'U', label: 'Unknown' },
];

// USCDI Demographics Options
const RACE_OPTIONS = [
  { value: 'american_indian', label: 'American Indian or Alaska Native' },
  { value: 'asian', label: 'Asian' },
  { value: 'black', label: 'Black or African American' },
  { value: 'hawaiian', label: 'Native Hawaiian or Other Pacific Islander' },
  { value: 'white', label: 'White' },
  { value: 'other', label: 'Other' },
  { value: 'unknown', label: 'Unknown' },
];

const ETHNICITY_OPTIONS = [
  { value: 'hispanic', label: 'Hispanic or Latino' },
  { value: 'not_hispanic', label: 'Not Hispanic or Latino' },
  { value: 'unknown', label: 'Unknown' },
];

const PREFERRED_LANGUAGE_OPTIONS = [
  { value: 'english', label: 'English' },
  { value: 'spanish', label: 'Spanish' },
  { value: 'mandarin', label: 'Mandarin' },
  { value: 'cantonese', label: 'Cantonese' },
  { value: 'vietnamese', label: 'Vietnamese' },
  { value: 'korean', label: 'Korean' },
  { value: 'tagalog', label: 'Tagalog' },
  { value: 'arabic', label: 'Arabic' },
  { value: 'french', label: 'French' },
  { value: 'other', label: 'Other' },
];

const GENDER_IDENTITY_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'trans_male', label: 'Transgender Male' },
  { value: 'trans_female', label: 'Transgender Female' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not', label: 'Prefer Not to Answer' },
];

const SEXUAL_ORIENTATION_OPTIONS = [
  { value: 'straight', label: 'Straight or Heterosexual' },
  { value: 'gay_lesbian', label: 'Gay or Lesbian' },
  { value: 'bisexual', label: 'Bisexual' },
  { value: 'other', label: 'Other' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'prefer_not', label: 'Prefer Not to Answer' },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

// Problem List Categories (SNOMED CT alignable)
const PROBLEM_CATEGORIES = [
  { value: 'Problem', label: 'Problem' },
  { value: 'Condition', label: 'Condition' },
  { value: 'Diagnosis', label: 'Diagnosis' },
  { value: 'Symptom', label: 'Symptom' },
  { value: 'Finding', label: 'Finding' },
  { value: 'Complaint', label: 'Complaint' },
];

// Clinical Status (FHIR R4 Condition clinicalStatus)
const CLINICAL_STATUS_OPTIONS = [
  { value: 'Active', label: 'Active', color: '#2E8B98' },
  { value: 'Recurrence', label: 'Recurrence', color: '#F26522' },
  { value: 'Relapse', label: 'Relapse', color: '#F26522' },
  { value: 'Inactive', label: 'Inactive', color: '#6C757D' },
  { value: 'Remission', label: 'Remission', color: '#62BB46' },
  { value: 'Resolved', label: 'Resolved', color: '#62BB46' },
];

// Verification Status (FHIR R4 Condition verificationStatus)
const VERIFICATION_STATUS_OPTIONS = [
  { value: 'Confirmed', label: 'Confirmed' },
  { value: 'Unconfirmed', label: 'Unconfirmed' },
  { value: 'Provisional', label: 'Provisional' },
  { value: 'Differential', label: 'Differential' },
  { value: 'Refuted', label: 'Refuted' },
  { value: 'Entered in Error', label: 'Entered in Error' },
];

// Severity (FHIR R4 Condition severity)
const SEVERITY_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'Mild', label: 'Mild' },
  { value: 'Moderate', label: 'Moderate' },
  { value: 'Severe', label: 'Severe' },
];

function Patients({ user, onNavigate }) {
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [physicians, setPhysicians] = useState([]);

  // Task #55 (Jun 8 2026): linked CRM contact for the currently-selected
  // patient. Null = no link or lookup still in flight. When set, the
  // detail toolbar shows a "View in CRM" button that deep-links into the
  // CRM contact detail surface (PEM journey, comms, etc.). Lookup is
  // gated by the crm:read permission server-side, so users without CRM
  // access simply never see the button populate.
  const [linkedContactId, setLinkedContactId] = useState(null);
  // Card P100 — link this chart to its CRM contact when it has none.
  const [showLinkContact, setShowLinkContact] = useState(false);
  /*
   * Card P100b item 2 — "From CRM contact". When a contact is picked the
   * five fields are prefilled and `fromContactId` rides along to
   * db:patients:create, which creates and links in ONE transaction.
   */
  const [fromContactId, setFromContactId] = useState(null);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [createDupes, setCreateDupes] = useState(null);
  // ⚠ The fields the duplicate result was computed for — card P100d item 2.
  const [dupeKey, setDupeKey] = useState(null);

  // View: list | detail | create | edit
  const [view, setView] = useState('list');
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const searchTimeout = useRef(null);

  // Problem List state
  const [problemList, setProblemList] = useState([]);
  const [showProblemForm, setShowProblemForm] = useState(false);
  const [problemForm, setProblemForm] = useState({
    snomedCode: '', snomedDisplay: '', icd10Code: '', icd10Display: '',
    clinicalStatus: 'Active', verificationStatus: 'Confirmed',
    category: 'Problem', severity: '', onsetDate: '', abatementDate: '', notes: ''
  });

  /*
   * ⛔⛔ OPEN EXACTLY THE PATIENT WE WERE SENT — Card H101 item 1, and this is
   * a WRONG-CHART defect found in production.
   *
   * #646 added "Open this person's chart" to the CRM contact header. It wrote
   * the patient id to sessionStorage and navigated here — and NOTHING EVER
   * READ IT. The key was written in CRMPage and consumed nowhere, so this
   * page simply kept whatever it was already showing:
   *
   *   first click  → the patient opened previously (a DIFFERENT person)
   *   second click → the bare list, nothing selected
   *
   * A doctor following that button could have read or written the wrong
   * chart. The reverse direction (cowork.crm.openContact) has had a reader in
   * CRMPage all along; I mirrored the write and not the read.
   *
   * ⚠ THE ID IS AUTHORITATIVE AND IS FETCHED BY ID. Not matched against the
   * loaded list — that list is filtered and paginated, so a hit depends on
   * what happened to be loaded. No fall back to the first patient, the last
   * selection, a list index, or a name search: if the id cannot be opened,
   * NOTHING is opened and the stale selection is CLEARED, because leaving the
   * previous patient on screen is the failure itself.
   */
  const [openChartError, setOpenChartError] = useState('');
  /*
   * ⛔⛔ LATEST REQUEST WINS — Card H101b item 2.
   *
   * The hand-off awaits getPatient and then calls selectPatient, which
   * itself awaits five more fetches. A user who clicks another patient
   * while that is in flight gets WHICHEVER FINISHES LAST — so the chart
   * could flip under them, which is the same wrong-chart hazard H101 item 1
   * was about, arriving a second later.
   *
   * ⚠ ONE token shared by both paths, so a manual click CANCELS a pending
   * hand-off and vice versa. Two separate guards would each be internally
   * consistent and still race each other.
   */
  const selectSeq = useRef(0);

  useEffect(() => {
    loadPatients();
    loadPhysicians();

    let cancelled = false;
    (async () => {
      let raw = null;
      try {
        raw = sessionStorage.getItem('cowork.patients.openPatient');
        // ⚠ Removed IMMEDIATELY, like the CRM side: a hand-off that survives
        // its navigation re-opens a chart nobody asked for on the next visit.
        if (raw !== null) sessionStorage.removeItem('cowork.patients.openPatient');
      } catch { /* sessionStorage unavailable — nothing to honour */ }
      if (!raw || cancelled) return;

      const id = parseInt(raw, 10);
      if (!Number.isFinite(id) || id <= 0) {
        setSelectedPatient(null); setView('list');
        setOpenChartError("This contact's patient record could not be opened.");
        return;
      }
      // ⛔ Card H101b item 2 — the hand-off takes a slot too, so a manual
      // click during this fetch wins and this response is dropped.
      const seq = ++selectSeq.current;
      try {
        const patient = await window.zignagenixAPI.getPatient(id);
        if (cancelled || seq !== selectSeq.current) return;
        if (!patient || !patient.id) {
          // ⛔ Not found, or not visible to this user — the server decides.
          setSelectedPatient(null); setView('list');
          setOpenChartError("This contact's patient record could not be opened.");
          return;
        }
        /*
         * ⛔⛔ AN INACTIVE CHART IS NOT THE ANSWER — Card H101b item 3.
         * db:patients:get has no is_active filter (and is NOT changed here —
         * other callers legitimately read retired records), so a contact
         * linked to a retired or de-duplicated patient opened it silently.
         * A clinician would have written into a chart nobody reads.
         */
        if (patient.is_active === false) {
          setSelectedPatient(null); setView('list');
          setOpenChartError('This contact is linked to an inactive patient record. '
            + 'Search Patients for the active chart.');
          return;
        }
        setOpenChartError('');
        await selectPatient(patient);
      } catch {
        /*
         * ⛔ Card H101c item 3 — A FAILED HAND-OFF MUST NOT BLANK A CHART THE
         * DOCTOR CHOSE. `cancelled` only covers unmount; it says nothing about
         * a manual click that happened while this fetch was in flight. Without
         * the seq check a hand-off that rejects late clears the patient and
         * throws the user back to the list with an error about a contact they
         * have already moved on from.
         */
        if (cancelled || seq !== selectSeq.current) return;
        setSelectedPatient(null); setView('list');
        setOpenChartError("This contact's patient record could not be opened.");
      }
    })();

    return () => {
      cancelled = true;
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, []);

  async function loadPatients() {
    try {
      const data = await window.zignagenixAPI.getPatients();
      setPatients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load patients:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadPhysicians() {
    try {
      const users = await window.zignagenixAPI.getUsers();
      if (Array.isArray(users)) {
        setPhysicians(users.filter(u => u.is_active && (u.role === 'physician' || u.role === 'admin')));
      }
    } catch (err) { /* ignore */ }
  }

  async function handleSearch(term) {
    setSearchTerm(term);
    clearTimeout(searchTimeout.current);
    if (term.length === 0) {
      loadPatients();
    } else if (term.length >= 2) {
      searchTimeout.current = setTimeout(async () => {
        try {
          const results = await window.zignagenixAPI.searchPatients(term);
          setPatients(Array.isArray(results) ? results : []);
        } catch (err) {
          console.error('Search failed:', err);
        }
      }, 300);
    }
  }

  async function selectPatient(patient) {
    const seq = ++selectSeq.current;          // ⛔ claim the latest slot
    // Reset prior link so the button doesn't briefly flash the wrong target
    // while transitioning between patients.
    setLinkedContactId(null);
    try {
      const [meds, labs, allergies, vitals, encounters] = await Promise.all([
        window.zignagenixAPI.getMedications(patient.id),
        window.zignagenixAPI.getLabResults(patient.id),
        window.zignagenixAPI.getAllergies(patient.id),
        window.zignagenixAPI.getVitals(patient.id),
        window.zignagenixAPI.getEncounters(patient.id),
      ]);
      // ⛔ A newer selection started while these were in flight — drop this
      // one rather than overwrite the chart the user is now looking at.
      if (seq !== selectSeq.current) return;
      setSelectedPatient({ ...patient, meds, labs, allergies, vitals, encounters });
      setView('detail');
    } catch (err) {
      console.error('Failed to load patient details:', err);
      /*
       * ⛔ Card H101c item 1 — THE FAILURE PATH NEEDS THE SAME GUARD AS THE
       * SUCCESS PATH. The success path above drops a stale response; this one
       * did not, so a detail fetch for A that REJECTS late still fell through
       * to setSelectedPatient(patient) and replaced B with A. A fetch that
       * fails is every bit as capable of showing the wrong chart as one that
       * succeeds — and it is the likelier race, because a request that errors
       * out often takes longer than one that works.
       */
      if (seq !== selectSeq.current) return;
      setSelectedPatient(patient);
      setView('detail');
    }
    // Task #55: fire-and-forget CRM contact lookup. Runs after the
    // clinical detail load so it never blocks the primary view. Lookup
    // failures are silent — the button just doesn't appear.
    try {
      const r = await window.zignagenixAPI.invoke('crm:contact-for-patient', patient.id);
      /*
       * ⛔ Card H101c item 2 — A STALE LOOKUP PUTS A'S CONTACT ON B'S CHART.
       * linkedContactId is page-level, not per-patient, so A's slow lookup
       * landing after B is open gives B an "Open CRM contact" button that
       * opens A's contact. Worse than a missing button: the clinician is on
       * B's chart and the button is right there, so they have no reason to
       * doubt it. setLinkedContactId(null) at the top of selectPatient clears
       * it for the new patient; only this guard stops the old one re-setting
       * it afterwards.
       */
      if (seq !== selectSeq.current) return;
      if (r && Number.isFinite(r.contactId) && r.contactId > 0) {
        setLinkedContactId(r.contactId);
      }
    } catch (err) {
      // crm:read denied or lookup failed — silently skip showing the link
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[Patients] CRM link lookup skipped:', err?.message);
      }
    }
  }

  // ─── Form helpers ────────────────────────────────────────────────────

  /*
   * ⛔⛔ CARD P100d ITEM 1 — A STALE CONTACT LINKED THE WRONG PATIENT.
   *
   * `fromContactId` was page-level state cleared only by the Clear button.
   * Pick contact X, press Cancel, then Add Patient for Y, and Y was created
   * AND LINKED TO X's CONTACT — X's candidacy, safety screen and proposals
   * then sit on Y's chart, and the next plain create is refused
   * CONTACT_ALREADY_LINKED with no explanation the doctor can act on.
   *
   * ⇒ One function resets every piece of from-contact state, and every exit
   * from the form calls it. A reset scattered across call sites is how one
   * of them gets missed.
   */
  function resetFromContact() {
    setFromContactId(null);
    setCreateDupes(null);
    setDupeKey(null);
    setShowContactPicker(false);
  }

  function openCreateForm() {
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setSaveMessage('');
    resetFromContact();          // ⛔ a fresh form carries no previous contact
    setView('create');
  }

  function openEditForm() {
    if (!selectedPatient) return;
    setForm({
      firstName: selectedPatient.first_name || '',
      lastName: selectedPatient.last_name || '',
      dateOfBirth: selectedPatient.date_of_birth || '',
      gender: selectedPatient.gender || 'U',
      phone: selectedPatient.phone || '',
      email: selectedPatient.email || '',
      address: selectedPatient.address || '',
      city: selectedPatient.city || '',
      state: selectedPatient.state || '',
      zip: selectedPatient.zip || '',
      insuranceProvider: selectedPatient.insurance_provider || '',
      insuranceId: selectedPatient.insurance_id || '',
      primaryPhysicianId: selectedPatient.primary_physician_id || '',
      // USCDI Demographics
      race: selectedPatient.race || '',
      ethnicity: selectedPatient.ethnicity || '',
      preferredLanguage: selectedPatient.preferred_language || '',
      sexualOrientation: selectedPatient.sexual_orientation || '',
      genderIdentity: selectedPatient.gender_identity || '',
      emergencyContactName: selectedPatient.emergency_contact_name || '',
      emergencyContactPhone: selectedPatient.emergency_contact_phone || '',
      emergencyContactRelation: selectedPatient.emergency_contact_relation || '',
      ssn4: selectedPatient.ssn4 || '',
      // ICD-10-CM & SNOMED CT
      icd10Code: selectedPatient.icd10_code || '',
      icd10Description: selectedPatient.icd10_description || '',
      isPrimaryDiagnosis: selectedPatient.is_primary_diagnosis || false,
      snomedCode: selectedPatient.snomed_code || '',
    });
    setFormErrors({});
    setSaveMessage('');
    setView('edit');
  }

  function updateField(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: null }));
  }

  /** Treat common "empty" placeholders as blank */
  function isNoneValue(v) {
    if (!v) return true;
    const lc = v.trim().toLowerCase();
    return ['none', 'n/a', 'na', 'nil', '-', '--', 'null', ''].includes(lc);
  }

  function validateForm() {
    const errors = {};
    if (!form.firstName.trim()) errors.firstName = 'First name is required';
    if (!form.lastName.trim()) errors.lastName = 'Last name is required';
    if (!form.dateOfBirth) errors.dateOfBirth = 'Date of birth is required';
    const emailVal = isNoneValue(form.email) ? '' : form.email;
    if (emailVal && !/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(emailVal)) errors.email = 'Invalid email format';
    if (form.zip && !/^\d{5}(-\d{4})?$/.test(form.zip)) errors.zip = 'Invalid ZIP (use 12345 or 12345-6789)';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  // ─── Problem List helpers ──────────────────────────────────────────────

  function updateProblemField(field, value) {
    setProblemForm(prev => ({ ...prev, [field]: value }));
  }

  function handleAddProblem() {
    if (!problemForm.snomedCode && !problemForm.icd10Code) {
      alert('Please enter at least a SNOMED CT Code or ICD-10-CM Code');
      return;
    }
    const newProblem = {
      id: Date.now(),
      ...problemForm
    };
    setProblemList(prev => [...prev, newProblem]);
    setProblemForm({
      snomedCode: '', snomedDisplay: '', icd10Code: '', icd10Display: '',
      clinicalStatus: 'Active', verificationStatus: 'Confirmed',
      category: 'Problem', severity: '', onsetDate: '', abatementDate: '', notes: ''
    });
    setShowProblemForm(false);
  }

  function handleRemoveProblem(id) {
    setProblemList(prev => prev.filter(p => p.id !== id));
  }

  async function handleCreate() {
    if (!validateForm()) return;
    setSaving(true);
    setSaveMessage('');
    try {
      // Sanitize "none/n/a" placeholders → null before sending to API
      const sanitized = { ...form };
      ['email', 'phone', 'address', 'city', 'insuranceProvider', 'insuranceId'].forEach(k => {
        if (isNoneValue(sanitized[k])) sanitized[k] = '';
      });
      /*
       * ⛔ DUPLICATES BEFORE THE SAVE, ONCE. A second chart for the same
       * human splits their candidacy and safety screen. The check runs on
       * the first attempt and the doctor must look before the second.
       */
      /*
       * ⛔⛔ CARD P100d ITEM 2 — THE CHECK USED TO RUN ONCE PER PAGE VISIT.
       * `if (createDupes === null)` meant every later create in the same
       * visit skipped it, and editing the name or DOB after a server error
       * did not re-check. A stale "no duplicates" is worse than none: it is
       * a confident answer about a different person.
       */
      const key = duplicateKey(sanitized);
      if (createDupes === null || dupeKey !== key) {
        const found = await findPossibleDuplicates(sanitized);
        setCreateDupes(found);
        setDupeKey(key);
        if (found.matches.length > 0) {
          setSaveMessage('A patient record already looks like this person — review below, then save again to create a new one.');
          return;
        }
      }
      // ⚠ contactId makes the create AND the link one transaction.
      if (fromContactId) sanitized.contactId = fromContactId;
      const result = await window.zignagenixAPI.createPatient(sanitized);
      console.log('[Patients] createPatient result:', JSON.stringify(result));
      if (result?.error) {
        // RBAC or database returned an error object
        setSaveMessage(result.message || result.code || 'Create failed — access denied or database error');
        return;
      }
      if (result?.success) {
        setSaveMessage(`Patient created — MRN: ${result.mrn}`);
        resetFromContact();      // ⛔ the next create must not reuse this contact
        await loadPatients();
        if (result.id) {
          try {
            const newPatient = await window.zignagenixAPI.getPatient(result.id);
            if (newPatient && !newPatient.error) {
              await selectPatient(newPatient);
            } else {
              setView('list');
            }
          } catch (fetchErr) {
            console.error('[Patients] Failed to fetch new patient:', fetchErr);
            setView('list');
          }
        } else {
          setView('list');
        }
      } else {
        setSaveMessage(result?.message || 'Create failed — no success response');
      }
    } catch (err) {
      console.error('[Patients] Create error:', err);
      setSaveMessage('Error: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate() {
    if (!validateForm()) return;
    setSaving(true);
    setSaveMessage('');
    try {
      const result = await window.zignagenixAPI.updatePatient(selectedPatient.id, form);
      if (result?.success) {
        setSaveMessage('Patient updated successfully');
        await loadPatients();
        const refreshed = await window.zignagenixAPI.getPatient(selectedPatient.id);
        if (refreshed && !refreshed.error) {
          await selectPatient(refreshed);
        }
      } else {
        setSaveMessage(result?.message || 'Update failed');
      }
    } catch (err) {
      setSaveMessage('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivate() {
    setSaving(true);
    try {
      const result = await window.zignagenixAPI.deactivatePatient(selectedPatient.id);
      if (result?.success) {
        setShowDeactivateConfirm(false);
        setSelectedPatient(null);
        setView('list');
        await loadPatients();
      } else {
        setSaveMessage(result?.message || 'Deactivation failed');
      }
    } catch (err) {
      setSaveMessage('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  // ─── Render Problem List section ──────────────────────────────────────

  function renderProblemListSection() {
    return (
      <div className="detail-section">
        {/* FHIR R4 Condition Resource — maps to: resourceType: "Condition", code: {coding: [{system: "http://snomed.info/sct", code: snomedCode}]}, clinicalStatus, verificationStatus, category, severity, onsetDateTime, abatementDateTime */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 className="detail-section-title">Problem List (SNOMED CT)</h3>
          {user?.permissions?.includes('patients:update') && (
            <button className="btn-sm btn-primary" onClick={() => setShowProblemForm(!showProblemForm)}>
              <Plus size={14} /> Add Problem
            </button>
          )}
        </div>

        {/* Problem Form */}
        {showProblemForm && (
          <div style={{ background: '#f8f9fa', border: 'none', boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#2E8B98', fontSize: '0.95rem', fontWeight: '600' }}>New Problem Entry</h4>

            {/* Row 1: SNOMED CT & Display */}
            <div className="patient-form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div className="form-group">
                <label className="form-label">SNOMED CT Code</label>
                <input className="form-input" value={problemForm.snomedCode}
                  onChange={(e) => updateProblemField('snomedCode', e.target.value)}
                  placeholder="e.g., 73211009" />
              </div>
              <div className="form-group">
                <label className="form-label">SNOMED Display Term</label>
                <input className="form-input" value={problemForm.snomedDisplay}
                  onChange={(e) => updateProblemField('snomedDisplay', e.target.value)}
                  placeholder="e.g., Diabetes Mellitus" />
              </div>
            </div>

            {/* Row 2: ICD-10 Code & Display */}
            <div className="patient-form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div className="form-group">
                <label className="form-label">ICD-10-CM Code</label>
                <input className="form-input" value={problemForm.icd10Code}
                  onChange={(e) => updateProblemField('icd10Code', e.target.value.toUpperCase())}
                  placeholder="e.g., E11.9" />
              </div>
              <div className="form-group">
                <label className="form-label">ICD-10-CM Display</label>
                <input className="form-input" value={problemForm.icd10Display}
                  onChange={(e) => updateProblemField('icd10Display', e.target.value)}
                  placeholder="e.g., Type 2 diabetes..." />
              </div>
            </div>

            {/* Row 3: Category, Clinical Status, Verification */}
            <div className="patient-form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-input" value={problemForm.category}
                  onChange={(e) => updateProblemField('category', e.target.value)}>
                  {PROBLEM_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Clinical Status</label>
                <select className="form-input" value={problemForm.clinicalStatus}
                  onChange={(e) => updateProblemField('clinicalStatus', e.target.value)}>
                  {CLINICAL_STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Verification Status</label>
                <select className="form-input" value={problemForm.verificationStatus}
                  onChange={(e) => updateProblemField('verificationStatus', e.target.value)}>
                  {VERIFICATION_STATUS_OPTIONS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div>
            </div>

            {/* Row 4: Severity, Onset Date, Abatement Date */}
            <div className="patient-form-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Severity</label>
                <select className="form-input" value={problemForm.severity}
                  onChange={(e) => updateProblemField('severity', e.target.value)}>
                  {SEVERITY_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Onset Date</label>
                <input type="date" className="form-input" value={problemForm.onsetDate}
                  onChange={(e) => updateProblemField('onsetDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Abatement Date</label>
                <input type="date" className="form-input" value={problemForm.abatementDate}
                  onChange={(e) => updateProblemField('abatementDate', e.target.value)} />
              </div>
            </div>

            {/* Row 5: Notes */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label">Notes</label>
              <textarea className="form-input" value={problemForm.notes}
                onChange={(e) => updateProblemField('notes', e.target.value)}
                placeholder="Clinical notes, observations, etc."
                style={{ minHeight: '80px', resize: 'vertical' }} />
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn-sm btn-primary" onClick={handleAddProblem}>
                <Save size={14} /> Save Problem
              </button>
              <button className="btn-sm btn-ghost" onClick={() => setShowProblemForm(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Problem List Display */}
        {problemList.length > 0 ? (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {problemList.map((problem) => {
              const statusOption = CLINICAL_STATUS_OPTIONS.find(s => s.value === problem.clinicalStatus);
              const statusColor = statusOption?.color || '#6C757D';
              return (
                <div key={problem.id} style={{
                  border: 'none', borderRadius: '12px',
                  padding: '1rem', background: '#F8F9FA',
                  boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {problem.snomedCode && (
                        <span style={{
                          background: '#e7f4f7', color: '#2E8B98', padding: '0.25rem 0.75rem',
                          borderRadius: '0.25rem', fontSize: '0.85rem', fontWeight: '600'
                        }}>
                          SNOMED: {problem.snomedCode}
                        </span>
                      )}
                      {problem.icd10Code && (
                        <span style={{
                          background: '#f0f4f8', color: '#495057', padding: '0.25rem 0.75rem',
                          borderRadius: '0.25rem', fontSize: '0.85rem', fontWeight: '600'
                        }}>
                          ICD-10: {problem.icd10Code}
                        </span>
                      )}
                      <span style={{
                        background: statusColor, color: '#fff', padding: '0.25rem 0.75rem',
                        borderRadius: '0.25rem', fontSize: '0.85rem', fontWeight: '600'
                      }}>
                        {problem.clinicalStatus}
                      </span>
                    </div>
                    {user?.permissions?.includes('patients:update') && (
                      <button className="btn-sm btn-ghost" style={{ color: '#DC3545' }}
                        onClick={() => handleRemoveProblem(problem.id)}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>

                  {(problem.snomedDisplay || problem.icd10Display) && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      {problem.snomedDisplay && <div className="detail-row"><strong>SNOMED:</strong> {problem.snomedDisplay}</div>}
                      {problem.icd10Display && <div className="detail-row"><strong>ICD-10:</strong> {problem.icd10Display}</div>}
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    {problem.category && <div className="detail-row"><strong>Category:</strong> {problem.category}</div>}
                    {problem.verificationStatus && <div className="detail-row"><strong>Verification:</strong> {problem.verificationStatus}</div>}
                    {problem.severity && <div className="detail-row"><strong>Severity:</strong> {problem.severity}</div>}
                    {problem.onsetDate && <div className="detail-row"><strong>Onset:</strong> {problem.onsetDate}</div>}
                    {problem.abatementDate && <div className="detail-row"><strong>Resolved:</strong> {problem.abatementDate}</div>}
                  </div>

                  {problem.notes && (
                    <div className="detail-row" style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: 'none' }}>
                      <strong>Notes:</strong> {problem.notes}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p style={{ color: '#6C757D', fontStyle: 'italic' }}>No problems recorded</p>
        )}
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────

  if (loading) return <div className="page-loading">Loading patients...</div>;

  // ─── Patient Form (shared between create and edit) ───────────────────
  function renderForm() {
    const isEdit = view === 'edit';
    return (
      <div className="patient-form">
        <div className="patient-form-header">
          <button
            className="btn-sm btn-ghost"
            onClick={() => { resetFromContact(); setView(isEdit ? 'detail' : 'list'); }}
          >
            <ChevronLeft size={16} /> Back
          </button>
          <h2>{isEdit ? 'Edit Patient' : 'Register New Patient'}</h2>
        </div>

        {/*
          ⛔⛔ CARD P100b ITEM 2 — START FROM A CRM CONTACT.
          The physician workflow (candidacy, safety screen, proposals) hangs
          off the CRM contact, so a chart created with no contact behind it
          cannot reach any of it. Picking one here prefills the five shared
          fields AND links the two records in the SAME transaction on save.
          ⚠ Offered only when creating, and only to users who can read CRM —
          treating_physician without crm:read simply does not see it.
        */}
        {!isEdit && user?.permissions?.includes('crm:read') && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0' }}>
            {fromContactId ? (
              <>
                <span style={{ fontSize: 13, color: '#15803D' }}>
                  Prefilled from CRM contact #{fromContactId} — it will be linked on save.
                </span>
                <button
                  className="btn-xs btn-ghost"
                  onClick={() => { setFromContactId(null); setCreateDupes(null); }}
                >
                  Clear
                </button>
              </>
            ) : (
              <button className="btn-sm btn-ghost" onClick={() => setShowContactPicker(true)}>
                From CRM contact
              </button>
            )}
          </div>
        )}
        {showContactPicker && (
          <ContactPickerModal
            onCancel={() => setShowContactPicker(false)}
            onPick={(c) => {
              /*
               * ⚠ EXACTLY FIVE FIELDS, and nothing else is touched. The rest
               * of the chart is the doctor's to fill in; silently importing
               * more of a sales record into a clinical one is how a wrong
               * address ends up on a prescription.
               */
              setForm((f) => ({
                ...f,
                firstName: c.first_name || '',
                lastName: c.last_name || '',
                dateOfBirth: (c.dob || c.date_of_birth) ? String(c.dob || c.date_of_birth).slice(0, 10) : '',
                email: c.email || '',
                phone: c.phone || '',
              }));
              setFromContactId(c.id);
              setCreateDupes(null);
              setShowContactPicker(false);
            }}
          />
        )}
        {createDupes && createDupes.matches.length > 0 && (
          <DuplicateWarning
            matches={createDupes.matches}
            checked={createDupes.checked}
            onLinkExisting={async (patient) => {
              if (!fromContactId) { setSaveMessage('Open this patient from the list to link it.'); return; }
              const r = await window.zignagenixAPI.invoke('patients:link-contact',
                { patientId: patient.id, contactId: fromContactId });
              if (r && r.success) {
                setSaveMessage(`Linked to the existing record — MRN ${r.mrn}. No new patient was created.`);
                resetFromContact();   // ⛔ ditto — the contact is spoken for now
                setView('list');
                await loadPatients();
              } else {
                setSaveMessage((r && r.message) || 'The link was refused.');
              }
            }}
          />
        )}

        <div className="patient-form-grid">
          {/* Name row */}
          <div className="form-group">
            <label className="form-label">First Name *</label>
            <input className={`form-input ${formErrors.firstName ? 'form-input--error' : ''}`}
              value={form.firstName} onChange={(e) => updateField('firstName', e.target.value)}
              placeholder="First name" />
            {formErrors.firstName && <span className="form-error">{formErrors.firstName}</span>}
          </div>
          <div className="form-group">
            <label className="form-label">Last Name *</label>
            <input className={`form-input ${formErrors.lastName ? 'form-input--error' : ''}`}
              value={form.lastName} onChange={(e) => updateField('lastName', e.target.value)}
              placeholder="Last name" />
            {formErrors.lastName && <span className="form-error">{formErrors.lastName}</span>}
          </div>

          {/* DOB + Gender */}
          <div className="form-group">
            <label className="form-label">Date of Birth *</label>
            <input type="date" className={`form-input ${formErrors.dateOfBirth ? 'form-input--error' : ''}`}
              value={form.dateOfBirth} onChange={(e) => updateField('dateOfBirth', e.target.value)} />
            {formErrors.dateOfBirth && <span className="form-error">{formErrors.dateOfBirth}</span>}
          </div>
          <div className="form-group">
            <label className="form-label">Gender</label>
            <select className="form-input" value={form.gender} onChange={(e) => updateField('gender', e.target.value)}>
              {GENDER_OPTIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>

          {/* Contact */}
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" value={form.phone}
              onChange={(e) => updateField('phone', e.target.value)} placeholder="(555) 123-4567" />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input type="email" className={`form-input ${formErrors.email ? 'form-input--error' : ''}`}
              value={form.email} onChange={(e) => updateField('email', e.target.value)}
              placeholder="patient@email.com" />
            {formErrors.email && <span className="form-error">{formErrors.email}</span>}
          </div>

          {/* Address */}
          <div className="form-group form-group--full">
            <label className="form-label">Address</label>
            <input className="form-input" value={form.address}
              onChange={(e) => updateField('address', e.target.value)} placeholder="123 Main St" />
          </div>
          <div className="form-group">
            <label className="form-label">City</label>
            <input className="form-input" value={form.city}
              onChange={(e) => updateField('city', e.target.value)} placeholder="City" />
          </div>
          <div className="form-group">
            <label className="form-label">State</label>
            <select className="form-input" value={form.state} onChange={(e) => updateField('state', e.target.value)}>
              <option value="">Select...</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">ZIP</label>
            <input className={`form-input ${formErrors.zip ? 'form-input--error' : ''}`}
              value={form.zip} onChange={(e) => updateField('zip', e.target.value)} placeholder="12345" />
            {formErrors.zip && <span className="form-error">{formErrors.zip}</span>}
          </div>

          {/* Insurance */}
          <div className="form-group">
            <label className="form-label">Insurance Provider</label>
            <input className="form-input" value={form.insuranceProvider}
              onChange={(e) => updateField('insuranceProvider', e.target.value)} placeholder="Aetna, Blue Cross, etc." />
          </div>
          <div className="form-group">
            <label className="form-label">Insurance ID</label>
            <input className="form-input" value={form.insuranceId}
              onChange={(e) => updateField('insuranceId', e.target.value)} placeholder="Policy number" />
          </div>

          {/* Primary Physician */}
          <div className="form-group">
            <label className="form-label">Primary Physician</label>
            <select className="form-input" value={form.primaryPhysicianId}
              onChange={(e) => updateField('primaryPhysicianId', e.target.value ? Number(e.target.value) : null)}>
              <option value="">None assigned</option>
              {physicians.map(p => <option key={p.id} value={p.id}>{p.display_name}</option>)}
            </select>
          </div>

          {/* ═════ USCDI Demographics ═════ */}
          <div className="form-group form-group--full" style={{ gridColumn: '1 / -1', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: 'none' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#2E8B98', fontSize: '1.1rem', fontWeight: '600' }}>Demographics (USCDI)</h3>
          </div>

          {/* Race */}
          <div className="form-group">
            <label className="form-label">Race</label>
            <select className="form-input" value={form.race}
              onChange={(e) => updateField('race', e.target.value)}>
              <option value="">Select...</option>
              {RACE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          {/* Ethnicity */}
          <div className="form-group">
            <label className="form-label">Ethnicity</label>
            <select className="form-input" value={form.ethnicity}
              onChange={(e) => updateField('ethnicity', e.target.value)}>
              <option value="">Select...</option>
              {ETHNICITY_OPTIONS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </div>

          {/* Preferred Language */}
          <div className="form-group">
            <label className="form-label">Preferred Language</label>
            <select className="form-input" value={form.preferredLanguage}
              onChange={(e) => updateField('preferredLanguage', e.target.value)}>
              <option value="">Select...</option>
              {PREFERRED_LANGUAGE_OPTIONS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </div>

          {/* Gender Identity */}
          <div className="form-group">
            <label className="form-label">Gender Identity</label>
            <select className="form-input" value={form.genderIdentity}
              onChange={(e) => updateField('genderIdentity', e.target.value)}>
              <option value="">Select...</option>
              {GENDER_IDENTITY_OPTIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>

          {/* Sexual Orientation */}
          <div className="form-group">
            <label className="form-label">Sexual Orientation</label>
            <select className="form-input" value={form.sexualOrientation}
              onChange={(e) => updateField('sexualOrientation', e.target.value)}>
              <option value="">Select...</option>
              {SEXUAL_ORIENTATION_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {/* SSN Last 4 */}
          <div className="form-group">
            <label className="form-label">SSN (Last 4)</label>
            <input className="form-input" maxLength="4" value={form.ssn4}
              onChange={(e) => updateField('ssn4', e.target.value.replace(/\D/g, ''))}
              placeholder="1234" />
          </div>

          {/* Emergency Contact Name */}
          <div className="form-group">
            <label className="form-label">Emergency Contact Name</label>
            <input className="form-input" value={form.emergencyContactName}
              onChange={(e) => updateField('emergencyContactName', e.target.value)}
              placeholder="Contact name" />
          </div>

          {/* Emergency Contact Phone */}
          <div className="form-group">
            <label className="form-label">Emergency Contact Phone</label>
            <input className="form-input" value={form.emergencyContactPhone}
              onChange={(e) => updateField('emergencyContactPhone', e.target.value)}
              placeholder="(555) 987-6543" />
          </div>

          {/* Emergency Contact Relation */}
          <div className="form-group">
            <label className="form-label">Emergency Contact Relation</label>
            <input className="form-input" value={form.emergencyContactRelation}
              onChange={(e) => updateField('emergencyContactRelation', e.target.value)}
              placeholder="Spouse, Parent, etc." />
          </div>

          {/* ═════ Diagnosis Coding ═════ */}
          <div className="form-group form-group--full" style={{ gridColumn: '1 / -1', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: 'none' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#2E8B98', fontSize: '1.1rem', fontWeight: '600' }}>Diagnosis Coding (ICD-10-CM / SNOMED CT)</h3>
          </div>

          {/* ICD-10 Code */}
          <div className="form-group">
            <label className="form-label">ICD-10-CM Code</label>
            <input className="form-input" value={form.icd10Code}
              onChange={(e) => updateField('icd10Code', e.target.value.toUpperCase())}
              placeholder="e.g., E11.9" />
          </div>

          {/* SNOMED Code */}
          <div className="form-group">
            <label className="form-label">SNOMED CT Code</label>
            <input className="form-input" value={form.snomedCode}
              onChange={(e) => updateField('snomedCode', e.target.value)}
              placeholder="e.g., 73211009" />
          </div>

          {/* ICD-10 Description */}
          <div className="form-group form-group--full">
            <label className="form-label">Diagnosis Description</label>
            <input className="form-input" value={form.icd10Description}
              onChange={(e) => updateField('icd10Description', e.target.value)}
              placeholder="e.g., Type 2 diabetes mellitus without complications" />
          </div>

          {/* Primary Diagnosis Checkbox */}
          <div className="form-group" style={{ display: 'flex', alignItems: 'center' }}>
            <label className="form-label" style={{ margin: 0 }}>
              <input type="checkbox" checked={form.isPrimaryDiagnosis}
                onChange={(e) => updateField('isPrimaryDiagnosis', e.target.checked)}
                style={{ marginRight: '0.5rem', cursor: 'pointer' }} />
              Primary Diagnosis
            </label>
          </div>
        </div>

        <div className="patient-form-actions">
          <button className="btn-primary" onClick={isEdit ? handleUpdate : handleCreate} disabled={saving}>
            <Save size={16} /> {saving ? 'Saving...' : (isEdit ? 'Update Patient' : 'Register Patient')}
          </button>
          {/* ⛔ The SECOND exit from the form, and it was missed on the first
              pass — card P100d item 1 names both. A reset on one exit only is
              the same bug wearing a different button. */}
          <button
            className="btn-ghost"
            onClick={() => { resetFromContact(); setView(isEdit ? 'detail' : 'list'); }}
            disabled={saving}
          >
            Cancel
          </button>
          {saveMessage && <span className={`form-save-message ${saveMessage.startsWith('Error') ? 'form-save-message--error' : ''}`}>{saveMessage}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header-row">
        <h1 className="page-title">Patients</h1>
        <PrintButton pageTitle="Patients" subtitle="Patient Records" />
      </div>

      {/* ─── List View ─── */}
      {/*
        ⛔ CARD H101 ITEM 1 — THE FAILURE MUST BE VISIBLE. A hand-off that
        cannot be honoured used to leave the previous patient on screen, so
        the user believed they were looking at the right chart. Now nothing
        is opened AND the reason is stated.
      */}
      {openChartError && (
        <div
          role="alert"
          data-testid="open-chart-error"
          style={{
            margin: '12px 0', padding: '10px 12px', borderRadius: 6,
            border: '1px solid #DC3545', background: '#FFF5F5', color: '#842029',
          }}
        >
          {openChartError}
        </div>
      )}

      {(view === 'list') && (
        <>
          <div className="patients-toolbar">
            <div className="search-bar">
              <Search size={18} />
              <input type="text" placeholder="Search by name or MRN..."
                value={searchTerm} onChange={(e) => handleSearch(e.target.value)} className="search-input" />
            </div>
            {user?.permissions?.includes('patients:create') && (
              <button className="btn-primary" onClick={openCreateForm}>
                <Plus size={16} /> New Patient
              </button>
            )}
          </div>

          <div className="patient-list">
            {patients.map((p) => (
              <button key={p.id}
                className={`patient-card ${selectedPatient?.id === p.id ? 'patient-card--active' : ''}`}
                onClick={() => selectPatient(p)}>
                <div className="patient-card-icon"><User size={18} /></div>
                <div className="patient-card-info">
                  <span className="patient-card-name">{p.last_name}, {p.first_name}</span>
                  <span className="patient-card-meta">MRN: {p.mrn} | DOB: {p.date_of_birth}</span>
                  {p.physician_name && <span className="patient-card-meta">PCP: {p.physician_name}</span>}
                </div>
              </button>
            ))}
            {patients.length === 0 && <p className="empty-state">No patients found</p>}
          </div>
        </>
      )}

      {/* ─── Detail View ─── */}
      {view === 'detail' && selectedPatient && (
        <div className="patient-detail-view">
          {showLinkContact && selectedPatient && (
            <LinkSearchModal
              looking="contact"
              patientId={selectedPatient.id}
              onCancel={() => setShowLinkContact(false)}
              onDone={(r) => { setShowLinkContact(false); setLinkedContactId(r.contactId); }}
            />
          )}
          <div className="patient-detail-toolbar">
            <button className="btn-sm btn-ghost" onClick={() => { setView('list'); setSelectedPatient(null); }}>
              <ChevronLeft size={16} /> All Patients
            </button>
            <div className="patient-detail-actions">
              {/* Task #55: cross-link to the CRM contact for this patient.
                  Visible only when (a) a CRM contact is linked via
                  crm_contacts.patient_id, and (b) the current user has
                  crm:read (else the lookup would have failed silently). */}
              {/* ⛔ Card P100 — a chart with no CRM contact cannot reach the
                  physician workflow (candidacy, safety screen, proposals),
                  because all of it hangs off the contact. Offered only when
                  there is no link yet. */}
              {!linkedContactId && (
                <button className="btn-sm btn-ghost" onClick={() => setShowLinkContact(true)}>
                  Link CRM contact
                </button>
              )}
              {linkedContactId && (
                <button
                  className="btn-sm btn-ghost"
                  onClick={() => {
                    try {
                      sessionStorage.setItem('cowork.crm.openContact', String(linkedContactId));
                    } catch { /* sessionStorage unavailable — fall through */ }
                    if (typeof onNavigate === 'function') {
                      onNavigate('crm');
                    }
                  }}
                  title="Open this patient's CRM contact: journey, comms, opportunities"
                >
                  <ExternalLink size={14} /> View in CRM
                </button>
              )}
              {/* ⛔ P121 — ADMIN / SYSADMIN ONLY, mirroring the server's own
                  role list (routes/patient_contact_link.js:173). The component
                  renders nothing when there is no link or the role is wrong.
                  ⚠ The MRN is the one already on screen, not fetched for it.
                  onUnlinked refreshes THIS view: the header must stop offering
                  "View in CRM" and start offering "Link CRM contact" again, or
                  it would point at a link that is gone. */}
              <PatientSideUnlink
                user={user}
                linkedContactId={linkedContactId}
                mrn={selectedPatient && selectedPatient.mrn}
                onUnlinked={() => setLinkedContactId(null)}
              />
              {user?.permissions?.includes('patients:update') && (
                <button className="btn-sm btn-primary" onClick={openEditForm}>
                  <Edit3 size={14} /> Edit
                </button>
              )}
              {user?.permissions?.includes('patients:delete') && (
                <button className="btn-sm btn-danger" onClick={() => setShowDeactivateConfirm(true)}>
                  <Trash2 size={14} /> Deactivate
                </button>
              )}
            </div>
          </div>

          {/* Deactivate confirmation */}
          {showDeactivateConfirm && (
            <div className="confirm-banner confirm-banner--danger">
              <AlertTriangle size={16} />
              <span>Are you sure you want to deactivate <strong>{selectedPatient.first_name} {selectedPatient.last_name}</strong>? This will hide the patient from active records.</span>
              <button className="btn-sm btn-danger" onClick={handleDeactivate} disabled={saving}>
                {saving ? 'Deactivating...' : 'Confirm Deactivate'}
              </button>
              <button className="btn-sm btn-ghost" onClick={() => setShowDeactivateConfirm(false)}>Cancel</button>
            </div>
          )}

          <div className="patient-detail">
            <div className="detail-header">
              <h2>{selectedPatient.first_name} {selectedPatient.last_name}</h2>
              <span className="detail-mrn">{selectedPatient.mrn}</span>
            </div>

            <div className="detail-grid">
              <div className="detail-item"><User size={14} /> <span>DOB: {selectedPatient.date_of_birth} | Gender: {selectedPatient.gender}</span></div>
              {selectedPatient.phone && <div className="detail-item"><Phone size={14} /> <span>{selectedPatient.phone}</span></div>}
              {selectedPatient.email && <div className="detail-item"><Mail size={14} /> <span>{selectedPatient.email}</span></div>}
              {selectedPatient.address && (
                <div className="detail-item"><Building size={14} /> <span>
                  {selectedPatient.address}{selectedPatient.city ? `, ${selectedPatient.city}` : ''}
                  {selectedPatient.state ? `, ${selectedPatient.state}` : ''} {selectedPatient.zip || ''}
                </span></div>
              )}
              {selectedPatient.insurance_provider && <div className="detail-item"><Building size={14} /> <span>{selectedPatient.insurance_provider} ({selectedPatient.insurance_id})</span></div>}
            </div>

            {/* USCDI Demographics */}
            {(selectedPatient.race || selectedPatient.ethnicity || selectedPatient.preferred_language || selectedPatient.gender_identity || selectedPatient.sexual_orientation) && (
              <div className="detail-section">
                <h3 className="detail-section-title">Demographics (USCDI)</h3>
                {selectedPatient.race && <div className="detail-row">Race: {selectedPatient.race}</div>}
                {selectedPatient.ethnicity && <div className="detail-row">Ethnicity: {selectedPatient.ethnicity}</div>}
                {selectedPatient.preferred_language && <div className="detail-row">Preferred Language: {selectedPatient.preferred_language}</div>}
                {selectedPatient.gender_identity && <div className="detail-row">Gender Identity: {selectedPatient.gender_identity}</div>}
                {selectedPatient.sexual_orientation && <div className="detail-row">Sexual Orientation: {selectedPatient.sexual_orientation}</div>}
              </div>
            )}

            {/* Emergency Contact */}
            {(selectedPatient.emergency_contact_name || selectedPatient.emergency_contact_phone) && (
              <div className="detail-section">
                <h3 className="detail-section-title">Emergency Contact</h3>
                {selectedPatient.emergency_contact_name && <div className="detail-row">Name: {selectedPatient.emergency_contact_name}</div>}
                {selectedPatient.emergency_contact_relation && <div className="detail-row">Relation: {selectedPatient.emergency_contact_relation}</div>}
                {selectedPatient.emergency_contact_phone && <div className="detail-row">Phone: {selectedPatient.emergency_contact_phone}</div>}
              </div>
            )}

            {/* Diagnosis Coding */}
            {(selectedPatient.icd10_code || selectedPatient.snomed_code) && (
              <div className="detail-section">
                <h3 className="detail-section-title">Diagnosis Coding (ICD-10-CM / SNOMED CT)</h3>
                {selectedPatient.icd10_code && <div className="detail-row">ICD-10-CM Code: {selectedPatient.icd10_code}</div>}
                {selectedPatient.icd10_description && <div className="detail-row">Description: {selectedPatient.icd10_description}</div>}
                {selectedPatient.snomed_code && <div className="detail-row">SNOMED CT Code: {selectedPatient.snomed_code}</div>}
                {selectedPatient.is_primary_diagnosis && <div className="detail-row"><span style={{ color: '#2E8B98', fontWeight: '600' }}>✓ Primary Diagnosis</span></div>}
              </div>
            )}

            {/* Problem List (SNOMED CT) */}
            {renderProblemListSection()}

            {selectedPatient.allergies && selectedPatient.allergies.length > 0 && (
              <div className="detail-section">
                <h3 className="detail-section-title">Allergies</h3>
                {selectedPatient.allergies.map((a, i) => (
                  <div key={i} className={`allergy-tag allergy-tag--${a.severity || 'mild'}`}>
                    {a.allergen}{a.reaction ? ` — ${a.reaction}` : ''}{a.severity ? ` (${a.severity})` : ''}
                  </div>
                ))}
              </div>
            )}

            {selectedPatient.meds && selectedPatient.meds.length > 0 && (
              <div className="detail-section">
                <h3 className="detail-section-title">Active Medications</h3>
                {selectedPatient.meds.map((m, i) => (
                  <div key={i} className="detail-row">{m.drug_name} {m.dosage} — {m.frequency}</div>
                ))}
              </div>
            )}

            {selectedPatient.encounters && selectedPatient.encounters.length > 0 && (
              <div className="detail-section">
                <h3 className="detail-section-title">Recent Encounters</h3>
                {selectedPatient.encounters.slice(0, 5).map((e, i) => (
                  <div key={e.id || i} className="detail-row">
                    <span className={`status-badge status-badge--${e.status || 'unknown'}`}>{e.status}</span>
                    {e.encounter_date} — {(e.encounter_type || '').replace('_', ' ')} — {e.chief_complaint || 'No complaint recorded'}
                  </div>
                ))}
              </div>
            )}

            {selectedPatient.vitals && selectedPatient.vitals.length > 0 && (
              <div className="detail-section">
                <h3 className="detail-section-title">Recent Vitals</h3>
                {selectedPatient.vitals.slice(0, 3).map((v, i) => (
                  <div key={i} className="detail-row">
                    {v.recorded_at?.substring(0, 16)} —
                    {v.blood_pressure_sys && ` BP: ${v.blood_pressure_sys}/${v.blood_pressure_dia}`}
                    {v.heart_rate && ` HR: ${v.heart_rate}`}
                    {v.temperature_f && ` Temp: ${v.temperature_f}°F`}
                    {v.oxygen_saturation && ` SpO2: ${v.oxygen_saturation}%`}
                    {v.weight_lbs && ` Wt: ${v.weight_lbs} lbs`}
                  </div>
                ))}
              </div>
            )}

            {selectedPatient.labs && selectedPatient.labs.length > 0 && (
              <div className="detail-section">
                <h3 className="detail-section-title">Lab Results</h3>
                {selectedPatient.labs.slice(0, 5).map((l, i) => (
                  <div key={i} className={`detail-row ${l.is_abnormal ? 'detail-row--abnormal' : ''}`}>
                    {l.specimen_date} — {l.test_name}: {l.result_value} {l.result_unit}
                    {l.is_abnormal ? ' ⚠' : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Create / Edit Form ─── */}
      {(view === 'create' || view === 'edit') && renderForm()}
    </div>
  );
}

export default Patients;
